import type { DiscoveryApi } from '@backstage/core-plugin-api';
import type { FetchFunction } from '@ns/http-request';
import {
  type ArmAssertionSupplier,
  type EndpointOptions,
  queryResources,
  type QueryResourcesOptions,
  type QueryResourcesResult,
} from '@ns/plugin-arm-proxy-client';
import type { QueryFunctionContext } from '@tanstack/react-query';

/**
 * The Resource Graph query arguments that identify a cached answer.
 *
 * Transport, discovery, cancellation and the caller assertion are supplied by
 * the caller of {@link queryResourcesOptions}, not by this value:
 * `getAssertion` never belongs in the cache key.
 */
export type QueryResourcesQueryArgs = Omit<
  QueryResourcesOptions,
  'discoveryApi' | 'fetch' | 'getAssertion' | 'signal'
> & {
  /** Microsoft's options for the object-array result the pickers consume. */
  options?: EndpointOptions & { resultFormat?: 'objectArray' };
};

/** The Backstage dependencies that execute a Resource Graph query. */
export interface QueryResourcesQueryDependencies {
  discoveryApi: DiscoveryApi;
  fetch: FetchFunction;
  /** Obtains the caller's assertion per request, or `null` without an OBO identity. */
  getAssertion: ArmAssertionSupplier;
}

/** The cache identity shared by the hook and imperative callers. */
export type QueryResourcesQueryKey = readonly [
  'arm-proxy',
  'queryResources',
  QueryResourcesQueryArgs,
];

/**
 * Builds the TanStack Query recipe for one Resource Graph request.
 *
 * `resilience` deliberately remains outside the key: it changes retry and
 * timeout policy, not the requested Resource Graph data.
 * The returned recipe can be passed to a React Query hook or to an imperative
 * `QueryClient` call, so both consumers use the same cache identity and query
 * function.
 *
 * @param args - The Resource Graph query arguments that define the requested
 * data. Its resilience settings affect execution but not cache identity.
 * @param dependencies - The Backstage discovery and authenticated transport
 * dependencies used to execute the request.
 * @returns A TanStack Query key and query function for the request.
 */
export function queryResourcesOptions(
  args: QueryResourcesQueryArgs,
  dependencies: QueryResourcesQueryDependencies,
): {
  queryFn: (
    context: QueryFunctionContext<QueryResourcesQueryKey>,
  ) => Promise<QueryResourcesResult>;
  queryKey: QueryResourcesQueryKey;
} {
  const { resilience: _resilience, ...queryArgs } = args;
  const queryKey: QueryResourcesQueryKey = [
    'arm-proxy',
    'queryResources',
    queryArgs,
  ];

  return {
    queryFn: ({ signal }) =>
      queryResources({
        ...args,
        ...dependencies,
        signal,
      }),
    queryKey,
  };
}
